import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, mediaUrl } from "@/lib/oraya-api";
import { IMG } from "@/data/site";
import { Seo, Reveal, Overline } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";
import { ConstructonsStory } from "@/components/site/ConstructonsStory";

export default function SystemDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 oraya-container text-center" data-testid="system-notfound">
        <h1 className="display text-4xl text-ink">System not found</h1>
        <div className="mt-8"><OButton to="/systems" variant="secondary">Back to systems</OButton></div>
      </div>
    );
  }

  if (!p) return <div className="pt-40 pb-32 oraya-container text-subtle">Loading…</div>;

  return (
    <div className="pt-32 pb-32">
      <Seo title={`${p.name} — ORAYA`} description={p.tagline} path={`/systems/${p.slug}`} />
      <div className="oraya-container">
        <Link to="/systems" className="inline-flex items-center gap-2 text-subtle hover:text-ink transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> All systems
        </Link>
        <Reveal>
          <Overline>An ORAYA System</Overline>
          <h1 className="display text-5xl sm:text-6xl font-light tracking-tight text-gradient-mist mt-6">{p.name}</h1>
          <p className="text-xl md:text-2xl text-ink/85 mt-5 max-w-3xl">{p.tagline}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative rounded-3xl overflow-hidden mt-12 min-h-[320px] md:min-h-[480px] glass-heavy">
            <img src={mediaUrl(p.image) || IMG.constructons} alt={`${p.name} visualization`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface/80 to-transparent" />
          </div>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-12 mt-16">
          <div className="md:col-span-8">
            <p className="text-lg text-subtle leading-relaxed whitespace-pre-line">{p.description}</p>
            {p.positioning && <p className="text-ink mt-8 text-lg">{p.positioning}</p>}
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <div className="glass-medium rounded-2xl p-6">
              <Overline>System</Overline>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-subtle">Status</dt><dd className="text-ink capitalize">{p.status.replace("_", " ")}</dd></div>
                {p.industry && <div className="flex justify-between"><dt className="text-subtle">Industry</dt><dd className="text-ink">{p.industry}</dd></div>}
              </dl>
            </div>
            <div className="mt-6">
              <OButton to="/contact" variant="primary" icon data-testid="system-contact-btn">Talk to ORAYA</OButton>
            </div>
          </div>
        </div>
      </div>

      {p.slug === "constructons-ai" && <ConstructonsStory />}
    </div>
  );
}
